import { allFromSnapshot, firestoreFieldValueIncrement, } from "@buildwithflux/firestore-compatibility-layer";
/**
 * Firestore implementation of the KnowledgeBaseRepository
 */
export class FirestoreKnowledgeBaseRepository {
    db;
    constructor(db) {
        this.db = db;
    }
    /**
     * Add a new knowledge base suggestion
     * @param suggestionData The suggestion data to add
     * @returns A promise that resolves when the suggestion is added
     */
    async addKnowledgeSuggestion(suggestionData) {
        await this.db.knowledgeBaseSuggestionsCollection().doc(suggestionData.uid).set(suggestionData);
    }
    /**
     * Get knowledge base suggestions for a user
     * @param userUid The user ID to get suggestions for
     * @returns A promise that resolves to an array of knowledge base suggestions
     */
    async getKnowledgeSuggestions(userUid) {
        const snapshot = await this.db
            .knowledgeBaseSuggestionsCollection()
            .where("user_uid", "==", userUid)
            .orderBy("created_at", "desc")
            .get();
        return allFromSnapshot(snapshot);
    }
    /**
     * Get knowledge base notes for a user
     * @param userUid The user ID to get knowledge for
     * @returns A promise that resolves to an array of knowledge base entries
     */
    async getKnowledgeNotesForUser(userUid) {
        const snapshot = await this.db
            .knowledgeBaseNotesCollection()
            .where("user_uid", "==", userUid)
            .where("context", "in", ["user"])
            .orderBy("created_at", "desc")
            .get();
        return allFromSnapshot(snapshot);
    }
    /**
     * Get knowledge base notes for a  project
     * @param projectUid The project ID to get knowledge for
     * @returns A promise that resolves to an array of knowledge base entries
     */
    async getKnowledgeNotesForProject(projectUid) {
        const snapshot = await this.db
            .knowledgeBaseNotesCollection()
            .where("project_uid", "==", projectUid)
            .orderBy("created_at", "desc")
            .get();
        return allFromSnapshot(snapshot);
    }
    /**
     * Approve a knowledge base suggestion
     * @param suggestionUid The ID of the suggestion to approve
     * @param userUid The ID of the user approving the suggestion
     * @returns A promise that resolves when the suggestion is approved
     */
    async approveKnowledgeSuggestion(suggestionUid, _userUid) {
        const suggestionRef = this.db.knowledgeBaseSuggestionsCollection().doc(suggestionUid);
        await suggestionRef.update({
            status: "approved",
            updated_at: Date.now(),
        });
    }
    /**
     * Decline a knowledge base suggestion
     * @param suggestionUid The ID of the suggestion to decline
     * @returns A promise that resolves when the suggestion is declined
     */
    async declineKnowledgeSuggestion(suggestionUid) {
        const updatedData = {
            status: "declined",
            updated_at: Date.now(),
        };
        await this.db.knowledgeBaseSuggestionsCollection().doc(suggestionUid).update(updatedData);
    }
    /**
     * Update a knowledge base suggestion
     * @param suggestionUid The ID of the suggestion to update
     * @param knowledgeSuggestion The updated knowledge suggestion text
     * @param useWhen The updated use_when text
     * @param context The updated context value
     * @param wasEdited
     * @returns A promise that resolves when the suggestion is updated
     */
    async updateKnowledgeSuggestion(suggestionUid, knowledgeSuggestion, useWhen, context, wasEdited) {
        const updatedData = {
            knowledge: knowledgeSuggestion,
            updated_at: Date.now(),
        };
        if (useWhen !== undefined) {
            updatedData.use_when = useWhen;
        }
        if (context !== undefined) {
            updatedData.context = context;
        }
        if (wasEdited !== undefined) {
            updatedData.was_edited = wasEdited;
        }
        await this.db.knowledgeBaseSuggestionsCollection().doc(suggestionUid).update(updatedData);
    }
    /**
     * Update a knowledge base note
     * @param knowledgeUid The ID of the knowledge to update
     * @param knowledge The updated knowledge suggestion text
     * @returns A promise that resolves when the suggestion is updated
     */
    async updateKnowledgeNote(knowledgeUid, knowledge) {
        await this.db
            .knowledgeBaseNotesCollection()
            .doc(knowledgeUid)
            .update({
            ...knowledge,
            updated_at: Date.now(),
        });
    }
    /**
     * Delete a knowledge base note
     * @param knowledgeUid The ID of the knowledge entry to delete
     * @returns A promise that resolves when the entry is deleted
     */
    async deleteKnowledgeNote(knowledgeUid) {
        await this.db.knowledgeBaseNotesCollection().doc(knowledgeUid).delete();
    }
    /**
     * Subscribe to knowledge base suggestions for a user
     * @param userUid The user ID to get suggestions for
     * @param onReceive Callback for added or changed suggestions
     * @param onDelete Callback for removed suggestions
     * @param onError Callback for errors
     * @returns An unsubscriber function
     */
    subscribeToKnowledgeBaseSuggestions(userUid, onReceive, onDelete, onError) {
        return this.db
            .knowledgeBaseSuggestionsCollection()
            .where("user_uid", "==", userUid)
            .orderBy("created_at", "desc")
            .onSnapshot((snap) => {
            const addedOrChanged = [];
            const deleted = [];
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            snap.docChanges().forEach((change) => {
                if (change.type === "removed") {
                    deleted.push(change.doc.data());
                }
                else {
                    addedOrChanged.push(change.doc.data());
                }
            });
            onReceive(addedOrChanged);
            onDelete(deleted);
        }, onError);
    }
    /**
     * Update the run_id field of a knowledge base suggestion
     * @param suggestionUid The ID of the suggestion to update
     * @param runId The LangGraph run ID to associate with the suggestion
     * @returns A promise that resolves when the suggestion is updated
     */
    async updateKnowledgeBaseSuggestionRunId(suggestionUid, runId) {
        const updatedData = {
            run_id: runId,
            updated_at: Date.now(),
        };
        await this.db.knowledgeBaseSuggestionsCollection().doc(suggestionUid).update(updatedData);
    }
    async addToSystemKnowledgeModerationQueue(entry) {
        await this.db.systemKnowledgeModerationQueueCollection().doc(entry.uid).set(entry);
    }
    /**
     * Update the run_id field of a system knowledge moderation queue entry
     * @param entryUid The ID of the moderation queue entry to update
     * @param runId The LangGraph run ID to associate with the entry
     * @returns A promise that resolves when the entry is updated
     */
    async updateSystemKnowledgeModerationQueueRunId(entryUid, runId) {
        const updatedData = {
            run_id: runId,
            updated_at: Date.now(),
        };
        await this.db.systemKnowledgeModerationQueueCollection().doc(entryUid).update(updatedData);
    }
    async getSystemKnowledgeModerationQueue() {
        const snapshot = await this.db.systemKnowledgeModerationQueueCollection().orderBy("created_at", "desc").get();
        return allFromSnapshot(snapshot);
    }
    async getSystemKnowledgeModerationQueueCount(filters) {
        const baseQuery = this.db.systemKnowledgeModerationQueueCollection();
        const query = this.buildFilteredQuery(baseQuery, filters);
        const snapshot = await query.get();
        return snapshot.size;
    }
    async getSystemKnowledgeModerationQueueEntry(entryUid) {
        const doc = await this.db.systemKnowledgeModerationQueueCollection().doc(entryUid).get();
        if (!doc.exists) {
            return null;
        }
        return doc.data();
    }
    async approveSystemKnowledgeModerationEntry(entryUid, moderatorUid) {
        const entryRef = this.db.systemKnowledgeModerationQueueCollection().doc(entryUid);
        const entryDoc = await entryRef.get();
        if (!entryDoc.exists) {
            throw new Error(`Moderation entry with ID ${entryUid} not found`);
        }
        const entryData = entryDoc.data();
        if (!entryData) {
            throw new Error(`No data found for moderation entry with ID ${entryUid}`);
        }
        await entryRef.update({
            moderation_status: "approved",
            moderator_uid: moderatorUid,
            updated_at: Date.now(),
        });
    }
    async declineSystemKnowledgeModerationEntry(entryUid, moderatorUid, notes) {
        const updatedData = {
            moderation_status: "declined",
            moderator_uid: moderatorUid,
            updated_at: Date.now(),
        };
        if (notes) {
            updatedData.moderation_notes = notes;
        }
        await this.db.systemKnowledgeModerationQueueCollection().doc(entryUid).update(updatedData);
    }
    async updateSystemKnowledgeModerationEntry(entryUid, useWhen, knowledge) {
        await this.db.systemKnowledgeModerationQueueCollection().doc(entryUid).update({
            use_when: useWhen,
            knowledge: knowledge,
            was_edited: true,
            updated_at: Date.now(),
        });
    }
    async updateSystemKnowledgeModerationQueueSimilarEntries(entryUid, similarEntries) {
        await this.db.systemKnowledgeModerationQueueCollection().doc(entryUid).update({
            similar_entries: similarEntries,
            updated_at: Date.now(),
        });
    }
    async incrementKnowledgeEntryUsageCounter(knowledgeUid, counterType) {
        const fieldName = counterType === "chat_system_prompt"
            ? "accessed_via_chat_system_prompt_count"
            : "accessed_via_knowledge_tool_count";
        const increment = await firestoreFieldValueIncrement(this.db, 1);
        await this.db
            .knowledgeBaseNotesCollection()
            .doc(knowledgeUid)
            .update({
            [fieldName]: increment,
            updated_at: Date.now(),
        });
    }
    async incrementKnowledgeEntryReactionCounter(knowledgeUid, reactionType) {
        if (!knowledgeUid || knowledgeUid.trim() === "") {
            return;
        }
        const fieldName = reactionType === "upvote" ? "upvote_reaction_count" : "downvote_reaction_count";
        const increment = await firestoreFieldValueIncrement(this.db, 1);
        await this.db
            .knowledgeBaseNotesCollection()
            .doc(knowledgeUid)
            .update({
            [fieldName]: increment,
            updated_at: Date.now(),
        });
    }
    async revertSystemKnowledgeModerationEntry(entryUid, moderatorUid) {
        await this.db.systemKnowledgeModerationQueueCollection().doc(entryUid).update({
            moderation_status: "pending",
            moderator_uid: moderatorUid,
            updated_at: Date.now(),
        });
    }
    subscribeToSystemKnowledgeModerationQueue(onReceive, onDelete, onError) {
        return this.db
            .systemKnowledgeModerationQueueCollection()
            .orderBy("created_at", "desc")
            .onSnapshot((snap) => {
            const addedOrChanged = [];
            const deleted = [];
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            snap.docChanges().forEach((change) => {
                if (change.type === "removed") {
                    deleted.push(change.doc.data());
                }
                else {
                    addedOrChanged.push(change.doc.data());
                }
            });
            onReceive(addedOrChanged);
            onDelete(deleted);
        }, onError);
    }
    createSystemKnowledgeModerationStream(limit, filters) {
        let streamEnded = false;
        let lastDocument = null;
        const repo = this;
        return {
            fetchNextPage(onSuccess, onError) {
                if (streamEnded) {
                    onSuccess([]);
                    return () => { };
                }
                const baseQuery = repo.db.systemKnowledgeModerationQueueCollection();
                let query = repo.buildFilteredQuery(baseQuery, filters);
                query = query.orderBy("created_at", "desc").limit(limit);
                if (lastDocument) {
                    query = query.startAfter(lastDocument);
                }
                return query.onSnapshot((snapshot) => {
                    if (snapshot.empty) {
                        streamEnded = true;
                    }
                    lastDocument = snapshot.docs[snapshot.docs.length - 1] ?? null;
                    onSuccess(snapshot.docs.map((doc) => doc.data()));
                }, onError);
            },
            reset() {
                lastDocument = null;
                streamEnded = false;
            },
        };
    }
    async getKnowledgeBaseEntry(entryUid) {
        const doc = await this.db.knowledgeBaseNotesCollection().doc(entryUid).get();
        if (!doc.exists) {
            return null;
        }
        return doc.data();
    }
    async getSystemKnowledgeBaseEntries() {
        const snapshot = await this.db
            .knowledgeBaseNotesCollection()
            .where("context", "==", "system")
            .orderBy("created_at", "desc")
            .get();
        return allFromSnapshot(snapshot);
    }
    createSystemKnowledgeStream(limit, filters) {
        let streamEnded = false;
        let lastDocument = null;
        const repo = this;
        return {
            fetchNextPage(onSuccess, onError) {
                if (streamEnded) {
                    onSuccess([]);
                    return () => { };
                }
                let query = repo.db.knowledgeBaseNotesCollection().where("context", "==", "system");
                query = repo.buildSystemKnowledgeFilteredQuery(query, filters);
                query = query.limit(limit);
                if (lastDocument) {
                    query = query.startAfter(lastDocument);
                }
                return query.onSnapshot((snapshot) => {
                    if (snapshot.empty) {
                        streamEnded = true;
                    }
                    lastDocument = snapshot.docs[snapshot.docs.length - 1] ?? null;
                    onSuccess(snapshot.docs.map((doc) => doc.data()));
                }, onError);
            },
            reset() {
                lastDocument = null;
                streamEnded = false;
            },
        };
    }
    async getSystemKnowledgeBaseEntriesCount(filters) {
        let query = this.db.knowledgeBaseNotesCollection().where("context", "==", "system");
        query = this.buildSystemKnowledgeFilteredQuery(query, filters);
        const snapshot = await query.get();
        return snapshot.size;
    }
    buildFilteredQuery(baseQuery, filters) {
        let query = baseQuery;
        if (filters?.statusFilter) {
            query = query.where("moderation_status", "==", filters.statusFilter);
        }
        if (filters?.editStatusFilter) {
            if (filters.editStatusFilter === "edited") {
                query = query.where("was_edited", "==", true);
            }
            else if (filters.editStatusFilter === "not_edited") {
                query = query.where("was_edited", "!=", true);
            }
        }
        if (filters?.dateFilter) {
            const now = Date.now();
            let startTime;
            switch (filters.dateFilter) {
                case "today":
                    startTime = now - 24 * 60 * 60 * 1000;
                    break;
                case "last3days":
                    startTime = now - 3 * 24 * 60 * 60 * 1000;
                    break;
                case "last7days":
                    startTime = now - 7 * 24 * 60 * 60 * 1000;
                    break;
                case "last30days":
                    startTime = now - 30 * 24 * 60 * 60 * 1000;
                    break;
                default:
                    return query;
            }
            query = query.where("created_at", ">=", startTime);
        }
        if (filters?.sourceFilter) {
            query = query.where("source", "==", filters.sourceFilter);
        }
        return query;
    }
    buildSystemKnowledgeFilteredQuery(baseQuery, filters) {
        let query = baseQuery;
        if (filters?.dateFilter) {
            const now = Date.now();
            let startTime;
            switch (filters.dateFilter) {
                case "today":
                    startTime = now - 24 * 60 * 60 * 1000;
                    break;
                case "last3days":
                    startTime = now - 3 * 24 * 60 * 60 * 1000;
                    break;
                case "last7days":
                    startTime = now - 7 * 24 * 60 * 60 * 1000;
                    break;
                case "last30days":
                    startTime = now - 30 * 24 * 60 * 60 * 1000;
                    break;
                default:
                    return query;
            }
            query = query.where("created_at", ">=", startTime);
        }
        const sortBy = filters?.sortBy || "created_at";
        const sortOrder = filters?.sortOrder || "desc";
        if (sortBy === "chat_system_prompt_usage") {
            query = query.orderBy("accessed_via_chat_system_prompt_count", sortOrder);
        }
        else {
            query = query.orderBy(sortBy, sortOrder);
        }
        return query;
    }
}
//# sourceMappingURL=FirestoreKnowledgeBaseRepository.js.map